import { useState } from "react";
import { Box, Button, CircularProgress, Link, Stack, styled } from "@mui/material";
import { AttachFileOutlined } from "@mui/icons-material";
import toast from "react-hot-toast";

const ACCEPTED_TYPES =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const FileChip = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 12px",
  height: "32px",
  backgroundColor: "#f4f4f4",
  borderRadius: "4px",
  fontSize: "13px",
  color: "#333",
  "& svg": {
    width: "16px",
  },
});

interface DocumentUploadProps {
  /** Form field the file is uploaded under, e.g. `terms_document` */
  field: string;
  /** Name of the currently stored document, if any */
  filename?: string;
  /** Called once the upload (or removal) has been saved */
  onUpdated: () => void;
  /**
   * The settings mutation from `useMutateRecruiterSettings`. Deliberately not
   * the recruiter mutation that ImageUpload uses: the terms document is a
   * settings field, and each upload path must invalidate exactly one query key
   * so the Settings dialog only has one refetch to absorb.
   */
  mutation: {
    mutate: (
      variables: Record<string, unknown>,
      options?: { onSuccess?: () => void; onError?: (error: unknown) => void }
    ) => void;
  };
  setFormError: (errors: unknown) => void;
}

/**
 * Upload a single document (PDF or Word) against the recruiter. Mirrors
 * ImageUpload, but the file is stored on the API's cloud disk rather than
 * Cloudinary so it can be attached to outgoing emails.
 */
export default function DocumentUpload({
  field,
  filename,
  onUpdated,
  mutation,
  setFormError,
}: DocumentUploadProps) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploading(true);
    onUpdated();

    mutation.mutate(
      { [field]: file },
      {
        onSuccess: () => {
          toast.success("Document uploaded");
          setUploading(false);
          setFormError({});
        },
        onError: (error: unknown) => {
          const errors =
            (error as { response?: { data?: { errors?: Record<string, string[]> } } })
              ?.response?.data?.errors ?? {};
          setFormError(errors);
          setUploading(false);
          toast.error("Failed to upload document");
        },
      }
    );

    // Allow the same file to be re-selected after a failed upload
    event.target.value = "";
  };

  const handleRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    onUpdated();
    mutation.mutate({ [`${field}_path`]: "" });
  };

  if (uploading) {
    return <CircularProgress size={24} color="secondary" />;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      {filename ? (
        <>
          <FileChip>
            <AttachFileOutlined />
            <span>{filename}</span>
          </FileChip>
          <Link href="#" onClick={handleRemove}>
            Remove
          </Link>
        </>
      ) : (
        <Button variant="contained" component="label">
          Upload
          <input hidden accept={ACCEPTED_TYPES} type="file" onChange={handleUpload} />
        </Button>
      )}
    </Stack>
  );
}
